import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { api } from '../../lib/api.js';
import { formatCurrency, formatDate } from '../../lib/formatters.js';

interface SettledItem {
  id: string;
  claimNumber: string;
  claimType: string;
  status: string;
  claimedAmount: string;
  approvedAmount: string | null;
  isoCurrencyCode: string;
  settledAt: string | null;
  distributorName: string;
}

export function SettledThisWeekPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [items, setItems] = useState<SettledItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get<SettledItem[]>('/approver/settled')
      .then(setItems)
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="space-y-6">
      <div>
        <button
          type="button"
          onClick={() => navigate('/approver')}
          className="text-xs text-slate-500 hover:text-copper mb-3 transition-colors block"
        >
          ← {t('approver.dashboard.title', { defaultValue: 'Approver Dashboard' })}
        </button>
        <h1 className="font-serif italic text-3xl text-ink">
          {t('approver.settled.title', { defaultValue: 'Settled This Week' })}
        </h1>
        <p className="text-slate-500 text-sm mt-1">
          {t('approver.settled.subtitle', { defaultValue: 'Claims paid in the last 7 days.' })}
        </p>
      </div>

      {loading ? (
        <div className="space-y-2 animate-pulse">
          {[0, 1, 2].map((i) => <div key={i} className="h-12 bg-slate-100 rounded" />)}
        </div>
      ) : items.length === 0 ? (
        <div className="py-16 text-center border border-hairline rounded bg-bone">
          <p className="text-sm text-slate-400">
            {t('approver.settled.empty', { defaultValue: 'No claims settled this week.' })}
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto border border-hairline rounded">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-hairline text-xs uppercase tracking-wide text-slate-500 bg-bone">
                <th className="text-left px-4 py-3">{t('claims.claimNumber')}</th>
                <th className="text-left px-4 py-3">{t('approver.queue.distributor', { defaultValue: 'Distributor' })}</th>
                <th className="text-left px-4 py-3">{t('claims.type')}</th>
                <th className="text-right px-4 py-3">{t('claims.amount')}</th>
                <th className="text-right px-4 py-3">{t('approver.settled.approvedAmount', { defaultValue: 'Approved' })}</th>
                <th className="text-left px-4 py-3">{t('approver.settled.settledAt', { defaultValue: 'Settled At' })}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-hairline">
              {items.map((item) => (
                <tr
                  key={item.id}
                  onClick={() => navigate(`/approver/claims/${item.id}`)}
                  className="hover:bg-bone cursor-pointer transition-colors"
                >
                  <td className="px-4 py-3 font-mono text-xs text-slate-600">{item.claimNumber}</td>
                  <td className="px-4 py-3 text-xs text-slate-600 max-w-[160px] truncate">{item.distributorName}</td>
                  <td className="px-4 py-3 text-xs text-slate-600">
                    {t(`wizard.claimTypes.${item.claimType}.label`, { defaultValue: item.claimType })}
                  </td>
                  <td className="px-4 py-3 text-right font-mono text-xs">
                    {formatCurrency(Number(item.claimedAmount), item.isoCurrencyCode)}
                  </td>
                  <td className="px-4 py-3 text-right font-mono text-xs text-success">
                    {item.approvedAmount
                      ? formatCurrency(Number(item.approvedAmount), item.isoCurrencyCode)
                      : '—'}
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-500">
                    {item.settledAt ? formatDate(item.settledAt) : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
